/**
 * SheetStatusBar — Iter 178 elite polish.
 *
 * The old bar showed six loose counter pills, then a divider, then Zoom,
 * then online/offline, then queue, then last-saved. Reads like a
 * developer's debug output. This pass:
 *
 *   • Counts collapse into one grouped strip (Pins · Markups · Measure ·
 *     Objects · Photos · Docs) — a single visual unit with tabular
 *     numerals and subtle key/value hierarchy.
 *   • Zero counts DIM instead of shouting "0" in white. Any non-zero
 *     count draws the eye.
 *   • System state (online / queue / last-saved) collapses into a
 *     right-hand cluster with a proper light status dot.
 *   • Bar height 32→30 px, tokened border, no more heavy divider lines
 *     between every stat.
 *
 * Shows REAL counts only — never fake metrics. Missing data → dim dash.
 */
import React, { useEffect, useState } from "react";
import { Wifi, WifiOff, Save, CloudUpload } from "lucide-react";
import { projectObjectsSummary } from "@/lib/markupsApi";
import { api } from "@/lib/api";
import { queueState } from "@/lib/offlineQueueV2";

export default function SheetStatusBar({
  projectId, sheetId,
  markupsCount, pinsCount,
  zoomPct, online = true,
  lastSavedAt,
  measurementsReloadKey,
}) {
  const [measurements, setMeasurements] = useState(null);
  const [objects, setObjects] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [docs, setDocs] = useState(null);
  const [queue, setQueue] = useState(queueState());
  useEffect(() => {
    const refresh = () => setQueue(queueState());
    refresh();
    const id = setInterval(refresh, 4000);
    window.addEventListener("forgedops:offline-queue", refresh);
    return () => { clearInterval(id); window.removeEventListener("forgedops:offline-queue", refresh); };
  }, []);

  useEffect(() => {
    let alive = true;
    if (!sheetId || !projectId) return;
    Promise.all([
      api.get(`/sheets/${sheetId}/measurements`).then((r) => Array.isArray(r.data) ? r.data.length : 0).catch(() => 0),
      projectObjectsSummary(projectId).then((s) => (s._forbidden || s._degraded ? null : (s.total ?? 0))),
      api.get(`/sheets/${sheetId}/photos`).then((r) => Array.isArray(r.data) ? r.data.length : 0).catch(() => null),
      api.get(`/projects/${projectId}/documents`).then((r) => (r.data?.documents?.length ?? 0)).catch(() => 0),
    ]).then(([m, o, p, d]) => {
      if (!alive) return;
      setMeasurements(m); setObjects(o); setPhotos(p); setDocs(d);
    });
    return () => { alive = false; };
    // measurementsReloadKey — LOW fix (real-plan cert iteration_183):
    // this MEASURE count previously only refetched on sheetId/projectId/
    // markupsCount change, so it went stale after every save/delete on
    // the same sheet while MARKUPS (a direct prop) stayed live.
  }, [sheetId, projectId, markupsCount, measurementsReloadKey]);

  return (
    <div
      data-testid="sheet-status-bar"
      className="border-t border-[var(--fop-border)] flex items-center text-[11px] font-mono uppercase tracking-wider gap-4 px-4"
      style={{
        height: 30,
        background: "linear-gradient(180deg, var(--fop-surface) 0%, var(--fop-surface-2) 100%)",
        boxShadow: "inset 0 1px 0 var(--fop-border)",
      }}
    >
      {/* Counts strip — single visual unit, subtle dot separators. */}
      <div className="flex items-center gap-3.5">
        <Stat label="Pins" value={pinsCount} testid="status-pins" />
        <Sep />
        <Stat label="Markups" value={markupsCount} testid="status-markups" />
        <Sep />
        <Stat label="Measure" value={measurements} testid="status-measurements" />
        <Sep />
        <Stat label="Objects" value={objects} testid="status-objects" />
        <Sep />
        <Stat label="Photos" value={photos} testid="status-photos" />
        <Sep />
        <Stat label="Docs" value={docs} testid="status-docs" />
      </div>

      <div className="flex-1" />

      {/* System-state cluster — dim by default, only lights up when
          something is actually happening. Iter 178.7 — Online/Offline
          indicator removed. Sync status lives on the OperationalStatusSpine
          (SyncPill), which only surfaces when disconnected. The bottom
          bar now shows counts + zoom only. */}
      <div className="flex items-center gap-3.5 text-[var(--fop-text-3)]">
        <Stat label="Zoom" value={zoomPct != null ? `${zoomPct}%` : null} testid="status-zoom" />
        {queue.count > 0 && (
          <>
            <Sep />
            <div
              className={`flex items-center gap-1.5 ${queue.dead > 0 ? "text-[var(--fop-danger,#ef4444)]" : "text-[var(--fop-warn,#f59e0b)]"}`}
              data-testid="status-queue"
              title={`${queue.pending} pending sync${queue.dead ? `, ${queue.dead} failed` : ""}`}
            >
              <CloudUpload size={12} />
              <span>
                {queue.pending} queued{queue.dead > 0 ? ` · ${queue.dead} failed` : ""}
              </span>
            </div>
          </>
        )}
        {lastSavedAt && (
          <>
            <Sep />
            <div className="flex items-center gap-1.5" data-testid="status-last-saved">
              <Save size={11} className="opacity-70" />
              <span>Saved {formatSavedTime(lastSavedAt)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  const isZero = value === 0 || value === "0" || value === "0%";
  const isEmpty = value == null;
  return (
    <div
      className={`flex items-center gap-1.5 ${isEmpty || isZero ? "opacity-40" : ""}`}
      data-testid={testid}
    >
      <span className="text-[var(--fop-text-3)]">{label}</span>
      <span className={`tabular-nums ${isEmpty || isZero ? "text-[var(--fop-text-3)]" : "text-[var(--fop-text)] font-bold"}`}>
        {isEmpty ? "—" : value}
      </span>
    </div>
  );
}

function Sep() {
  return (
    <span
      aria-hidden="true"
      className="inline-block w-px h-3.5 bg-[var(--fop-border-2)]/70"
    />
  );
}

function formatSavedTime(iso) {
  try {
    const d = new Date(iso);
    const now = Date.now();
    const ago = Math.floor((now - d.getTime()) / 1000);
    if (ago < 60) return "just now";
    if (ago < 3600) return `${Math.floor(ago / 60)}m ago`;
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}
